import * as HoverCard from '@radix-ui/react-hover-card'
import { useState } from 'react'
import z from 'zod'
import { OutcomeIcon } from './outcome-icon.tsx'
import * as styles from './outcome-tag.css.ts'
import { OutcomeTooltip, type ErrorPopoverVariant } from './outcome-tooltip.tsx'
import { type OutcomeType } from './outcome-types.ts'
import { getOutcomeLabel } from './outcome-utils.ts'

export const OutcomeTagSizeSchema = z.enum(['default', 'small'])
export type OutcomeTagSize = z.infer<typeof OutcomeTagSizeSchema>

export type OutcomeTagProps = {
  outcomeType: OutcomeType
  size?: OutcomeTagSize
  /**
   * Opens the linked drawer. When provided, the popover renders a Details link for every outcome except the short
   * Error variant; when omitted, no Details link renders.
   */
  onDetailsClick?: () => unknown
  /** Body copy for the long Error variant. When provided and outcomeType is `error`, the popover renders the long variant. */
  errorMessage?: string
}

export const OutcomeTag: React.FC<OutcomeTagProps> = ({
  outcomeType,
  size = 'default',
  onDetailsClick,
  errorMessage,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const errorVariant: ErrorPopoverVariant =
    outcomeType === 'error' && errorMessage !== undefined && errorMessage.length > 0 ? 'long' : 'short'

  const handleDetailsClick = onDetailsClick
    ? () => {
        setIsOpen(false)
        onDetailsClick()
      }
    : undefined

  return (
    <HoverCard.Root open={isOpen} onOpenChange={setIsOpen} openDelay={0} closeDelay={120}>
      <HoverCard.Trigger asChild>
        <button type="button" className={styles.tag({ outcomeType, size })}>
          <OutcomeIcon outcomeType={outcomeType} size="small" />
          <span className={styles.label}>{getOutcomeLabel(outcomeType)}</span>
        </button>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content side="bottom" align="start" sideOffset={4} className={styles.hoverCardContent}>
          <OutcomeTooltip
            outcomeType={outcomeType}
            errorVariant={errorVariant}
            errorMessage={errorMessage}
            onDetailsClick={handleDetailsClick}
          />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  )
}
